import React, { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";
import JsonLd from "@/components/JsonLd";
import SeoFooter from "@/components/SeoFooter";
import { findLocation, SERVICES, ORIGIN } from "@/data/seoPages";

const GOLD = "#C7A668";

export default function LocationPage({ lang: routeLang }) {
  const ctx = useLang();
  const lang = routeLang || ctx.lang;
  const L = (en, es) => (lang === "en" ? en : es);
  const { slug } = useParams();
  const loc = findLocation(slug, lang);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!loc) return <Navigate to={lang === "en" ? "/en" : "/"} replace />;

  const c = loc[lang];
  const esPath = `/ubicaciones/${loc.esSlug}`;
  const enPath = `/en/locations/${loc.enSlug}`;
  const canonical = ORIGIN + (lang === "en" ? enPath : esPath);
  const bookPath = lang === "en" ? "/en/book" : "/book";
  const sBase = lang === "en" ? "/en/services/" : "/servicios/";

  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfessionalService",
        name: `dukk — ${loc.airport}`,
        description: c.desc,
        url: canonical,
        image: `${ORIGIN}/og-image.png`,
        priceRange: "$$$",
        areaServed: { "@type": "City", name: c.kicker.split("·")[1]?.trim() || loc.airport },
        serviceType: lang === "en" ? "Aircraft detailing, cabin care and private jet cleaning" : "Detailing, cabin care y limpieza de jets privados",
        address: { "@type": "PostalAddress", addressCountry: "MX" },
        parentOrganization: { "@type": "Organization", name: "dukk", url: ORIGIN },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: L("Home", "Inicio"), item: ORIGIN + (lang === "en" ? "/en" : "/") },
          { "@type": "ListItem", position: 2, name: c.h1, item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="grain" style={{ minHeight: "100vh", paddingTop: 110 }}>
      <PageSeo esPath={esPath} enPath={enPath} title={c.title} description={c.desc} lang={lang} image={`${ORIGIN}/og/loc-${loc.key}-${lang}.png`} />
      <JsonLd data={jsonld} id="jsonld-location" />
      <article data-testid="location-page" style={{ maxWidth: 880, margin: "0 auto", padding: "0 clamp(20px,5vw,40px)" }}>
        <div className="font-mono" style={{ fontSize: 10.5, letterSpacing: ".18em", color: GOLD, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}><MapPin size={13} />{c.kicker.toUpperCase()} · {loc.airport}</div>
        <h1 style={{ fontSize: "clamp(28px,4.6vw,48px)", fontWeight: 400, margin: 0, letterSpacing: "-.02em", lineHeight: 1.1 }}>{c.h1}</h1>
        <p style={{ fontSize: "clamp(16px,2vw,19px)", color: "#B8BDC3", lineHeight: 1.65, margin: "22px 0 0", maxWidth: 720 }}>{c.intro}</p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "30px 0 8px" }}>
          <Link to={bookPath} data-testid="location-cta-book" className="tb-cta" style={{ background: "#F2EFE8", color: "#0a0a0a", fontWeight: 600, borderRadius: 4, padding: "12px 22px", fontSize: 14, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>{L("Book service", "Reservar servicio")}<ArrowRight size={15} /></Link>
          <Link to={lang === "en" ? "/en/priority" : "/priority"} style={{ color: GOLD, border: `1px solid ${GOLD}`, borderRadius: 4, padding: "12px 22px", fontSize: 14, textDecoration: "none" }}>{L("Priority request", "Solicitud prioritaria")}</Link>
        </div>

        <section style={{ marginTop: 44 }}>
          <h2 style={{ fontSize: "clamp(20px,2.6vw,26px)", fontWeight: 400, margin: "0 0 18px", letterSpacing: "-.01em" }}>{L("Services available at this base", "Servicios disponibles en esta base")}</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
            {SERVICES.map((s) => (
              <Link key={s.key} data-testid={`location-service-${s.key}`} to={sBase + s[`${lang}Slug`]} className="glass" style={{ display: "block", padding: "18px 20px", borderRadius: 6, textDecoration: "none", border: "1px solid rgba(255,255,255,.1)" }}>
                <div style={{ fontSize: 16, color: "#E7E4DD", letterSpacing: "-.01em" }}>{s[lang].h1.split(",")[0].split(":")[0]}</div>
                <div className="font-mono" style={{ fontSize: 11, color: GOLD, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>{L("VIEW", "VER")} <ArrowRight size={12} /></div>
              </Link>
            ))}
          </div>
        </section>

        <p style={{ fontSize: 14, color: "#7a808a", lineHeight: 1.7, margin: "40px 0 0", maxWidth: 720 }}>
          {L("dukk provides aesthetic readiness, cabin care and detailing only. We do not perform maintenance, repair or technical inspection. All service is subject to owner/operator authorization and FBO/hangar access.", "dukk presta servicios estéticos de readiness, cabin care y detailing. No realizamos mantenimiento, reparación ni inspección técnica. Todo servicio está sujeto a autorización del dueño/operador y acceso del FBO/hangar.")}
        </p>
      </article>
      <SeoFooter />
    </div>
  );
}
